import React, { useEffect, useRef, useState, useCallback } from "react";

import * as cornerstone from "@cornerstonejs/core";
import * as csTools3d from "@cornerstonejs/tools";
import {
  ToolGroupManager,
  Enums as csToolsEnums,
  ZoomTool,
  PanTool,
  RectangleROITool,
  StackScrollTool,
  addTool,
  WindowLevelTool,
  annotation
} from '@cornerstonejs/tools';
import Events from '@cornerstonejs/tools/enums/Events'
import dicomParser from "dicom-parser";
import * as cornerstoneWADOImageLoader from "@cornerstonejs/dicom-image-loader";
import { loadDicomZip, revokeDicomZipCache, formatLoadProgress } from "../utils/dicomZipLoader";
import { acquireRenderingEngine, releaseRenderingEngine, SHARED_ENGINE_ID } from "../utils/sharedRenderingEngine";

import { Spin } from "antd";
import RadiologyViewerInfo from "./RadiologyViewerInfo";
import RadiologyToolbar from "./RadiologyToolbar";
import WindowLevelSyncManager from "../utils/WindowLevelSyncManager";
import SliceScrollbar from "./SliceScrollbar";

// Global initialization tracking
let globalInitPromise = null;
const activeInstances = new Set();
const { MouseBindings } = csToolsEnums;

// Global variable to track which viewport user is interacting with
if (typeof window !== 'undefined' && !window.ACTIVE_RADIOLOGY_VIEWPORT) {
  window.ACTIVE_RADIOLOGY_VIEWPORT = null;
}

const initializeCornerstone = async () => {
  if (!globalInitPromise) {
    globalInitPromise = (async () => {
      try {
        if (!cornerstone.isCornerstoneInitialized()) {
          await cornerstone.init();
        }

        // Configure the WADO Image Loader to be more tolerant
        const wadoConfig = {
          useWebWorkers: false,
          decodeConfig: {
            convertFloatPixelDataToInt: false,
            use16BitDataType: true,
            // More tolerant error handling
            strict: false,
            usePDFJS: false
          }
        };

        try {
          // Initialize WADO Image Loader
          await cornerstoneWADOImageLoader.init(wadoConfig);

          // Access WADO URI loader directly if available
          if (cornerstoneWADOImageLoader.wadouri) {
            try {
              // Set up the image loader with more flexibility
              // This pattern works for versions that don't have the configure method
              if (cornerstoneWADOImageLoader.wadouri.external?.cornerstone?.imageLoader) {
                const imageLoader = cornerstoneWADOImageLoader.wadouri.external.cornerstone.imageLoader;
                imageLoader.preferSizeForImage = true; // Prefer the size in the file metadata
              }
            } catch (wadoUriError) {
              console.warn('Error configuring WADO URI loader:', wadoUriError);
              // Continue with default settings
            }
          } else {
            console.warn('WADO URI loader not found - DICOM loading may be limited');
          }
        } catch (error) {
          console.error('Error initializing WADO Image Loader:', error);
          // Continue anyway - we might still be able to use some functionality
        }

        await csTools3d.init();
      } catch (error) {
        console.error('Failed to initialize CornerstoneJS:', error);
        globalInitPromise = null;
        throw error;
      }
    })();
  }
  return globalInitPromise;
};

const StackRadiologyViewer = ({ itemId, seriesName, zipUrl, annotations = [], targetAnnotationId = null, showAnnotations = true, onAnnotationCompleted, userRole }) => {
  const viewerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(null);
  const lastProgressKeyRef = useRef("");
  // Progress fires once per network chunk; only re-render when the label changes.
  const handleLoadProgress = useCallback((p) => {
    const key = `${p.phase}:${p.pct}:${p.loaded}`;
    if (key === lastProgressKeyRef.current) return;
    lastProgressKeyRef.current = key;
    setLoadProgress(p);
  }, []);
  const [error, setError] = useState(null);
  const renderingEngineRef = useRef(null);
  const metadataRef = useRef(new Map());
  const mountedRef = useRef(true);
  const viewportReadyRef = useRef(false);
  const [activeTool, setActiveToolState] = useState(null);
  const userRoleRef = useRef(userRole);
  const [scrollSpeed, setScrollSpeed] = useState(1); // Default scroll speed
  const scrollSpeedRef = useRef(1);
  const scrollingTimerRef = useRef(null);
  const windowLevelSyncRef = useRef(null);
  const frameOfReferenceUIDRef = useRef(null);
  
  // Update userRoleRef when the prop changes
  useEffect(() => {
    userRoleRef.current = userRole;
  }, [userRole]);
  
  const instanceId = useRef(
    `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  ).current;
  
  // Initialize toolGroupIdRef after instanceId is defined
  const toolGroupIdRef = useRef(`toolgroup-${instanceId}`);
  
  const viewportId = `viewport-${instanceId}`;
  const renderingEngineId = SHARED_ENGINE_ID;
  const stackId = `stack-${seriesName}-${instanceId}`;

  const [currentSlice, setCurrentSlice] = useState(0);
  const totalSlicesRef = useRef(0);
  const [voiRange, setVoiRange] = useState({ windowWidth: 400, windowCenter: 40 });
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fsEl === containerRef.current);
    };
    document.addEventListener('fullscreenchange', handleChange);
    document.addEventListener('webkitfullscreenchange', handleChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
      document.removeEventListener('webkitfullscreenchange', handleChange);
    };
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsEl === el) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  }, []);

  // Update scroll speed ref when state changes
  useEffect(() => {
    scrollSpeedRef.current = scrollSpeed;
  }, [scrollSpeed]);

  // --- Annotation Navigation Effect ---
  useEffect(() => {
    if (!targetAnnotationId || !viewportReadyRef.current || !renderingEngineRef.current) return;

    const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);

    if (targetAnno && typeof targetAnno.sliceIndex === 'number') {
      const targetSliceIndex = targetAnno.sliceIndex;
      const viewport = renderingEngineRef.current.getViewport(viewportId);

      if (viewport) {
        try {
          // Verify target slice index is within bounds
          const imageIds = viewport.getImageIds();
          if (!imageIds || targetSliceIndex < 0 || targetSliceIndex >= imageIds.length) {
            console.warn(`[StackRadiologyViewer ${instanceId}] Invalid navigation target: slice ${targetSliceIndex} is out of bounds (0-${imageIds?.length - 1 || 0})`);
            return;
          }
          
          // Navigate to the correct slice
          viewport.setImageIdIndex(targetSliceIndex);
          
          // Find and highlight the annotation using cornerstone tools
          try {
            // Get all annotations from cornerstone state
            const allAnnotations = annotation.state.getAllAnnotations();
            
            // Reset highlighted state for all annotations
            allAnnotations.forEach(anno => {
              anno.highlighted = false;
            });
            
            // Find our target annotation by ID
            const targetCornerstoneAnno = allAnnotations.find(anno => 
              anno.annotationUID === targetAnnotationId
            );
            
            if (targetCornerstoneAnno) {
              // Set the annotation to be highlighted
              targetCornerstoneAnno.highlighted = true;
              
              // Apply style to make the annotation more visible
              const style = {
                textBoxVisibility: false,
                colorHighlighted: 'rgb(0, 255, 0)', // Bright cyan
                color: 'rgb(0, 255, 0)',
                isVisible: true,
                lineWidth: 3
              };
              
              // Apply style if the method is available
              try {
                if (annotation.config?.style?.setAnnotationStyles) {
                  annotation.config.style.setAnnotationStyles(targetAnnotationId, style);
                }
              } catch (styleErr) {
                console.warn(`[StackRadiologyViewer ${instanceId}] Error applying highlight style:`, styleErr);
              }
            } else {
              console.warn(`[StackRadiologyViewer ${instanceId}] Could not find annotation ${targetAnnotationId} to highlight, creating it now`);
              
              // If annotation isn't in cornerstone state, create and add it
              try {
                // Get the target annotation data
                const anno = targetAnno;

                // Get the image ID for this slice
                const targetSliceImageId = viewport.getImageIds()[anno.sliceIndex];

                if (!targetSliceImageId) {
                  console.warn(`[StackRadiologyViewer ${instanceId}] No image ID found for slice ${anno.sliceIndex}`);
                  return;
                }
                
                
              
                // Get proper world Z from image plane module
                const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', targetSliceImageId);

                // Calculate worldZ with proper spacing consideration
                let worldZ = imagePlaneModule?.imagePositionPatient?.[2] || 0;

                // Process points - check if the points already have valid Z values
                let points = [];
                
                // If the annotation was drawn by user, points may already have proper world Z coordinates
                if (anno.points.length === 4 &&
                    anno.points[0].length === 3 &&
                    Math.abs(anno.points[0][2]) > 1) {  // Existing world Z coordinates are typically large values
                  points = anno.points;
                } else {
                  // For rectangles with just two points (top-left and bottom-right)
                  const topLeft = anno.points[1];
                  const bottomRight = anno.points[0];
                  
                  // Make sure we have valid points with proper world Z
                  const validTopLeft = Array.isArray(topLeft) ? 
                      [topLeft[0], topLeft[1], worldZ] : 
                      [topLeft.x, topLeft.y, worldZ];
                      
                  const validBottomRight = Array.isArray(bottomRight) ? 
                      [bottomRight[0], bottomRight[1], worldZ] : 
                      [bottomRight.x, bottomRight.y, worldZ];
                  
                  // Create the other two corners with proper world Z
                  const topRight = [validBottomRight[0], validTopLeft[1], worldZ];
                  const bottomLeft = [validTopLeft[0], validBottomRight[1], worldZ];
                  
                  // Create all four points in the correct order for cornerstone
                  points = [validTopLeft, topRight, validBottomRight, bottomLeft];
                }
                
                // Create the cornerstone annotation object
                const cornerstoneAnnotation = {
                  annotationUID: anno.id,
                  metadata: {
                    toolName: 'RectangleROI',
                    viewportId: viewportId,
                    sourceItemId: itemId,
                    instanceId: instanceId
                  },
                  data: {
                    handles: {
                      points: points,
                      activeHandleIndex: null
                    },
                    label: anno.name || '',
                    cachedStats: {},
                    color: [0, 255, 0], // Green color
                    lineWidth: 3,
                    isVisible: true
                  },
                  highlighted: true, // Always highlight during navigation
                  isLocked: false,
                  isVisible: true
                };
                
                // Add to cornerstone state
                const annotationGroupSelector = {
                  viewportUID: viewportId
                };
                
                // --- Prevent Duplicates: Remove existing annotation first ---
                try {
                  annotation.state.removeAnnotation(anno.id);
                } catch (removeError) {
                  // Log specifically if removal failed for the preset annotation
                  if (anno.id === 'preset-rectangle') {
                    console.error(`[StackRadiologyViewer ${instanceId}] FAILED to remove existing annotation ${anno.id}:`, removeError);
                  } else if (!removeError.message?.includes('Cannot remove annotation')) {
                     console.warn(`[StackRadiologyViewer ${instanceId}] Error removing existing annotation ${anno.id}:`, removeError);
                  }
                }
                // ----------------------------------------------------------
                
                // Add to cornerstone state
                annotation.state.addAnnotation(cornerstoneAnnotation, annotationGroupSelector);
                
                // Apply style
                const style = {
                  textBoxVisibility: false,
                  colorHighlighted: 'rgb(0, 255, 0)', // Cyan for highlighted state
                  color: 'rgb(0, 255, 0)', // Green for normal state
                  isVisible: true,
                  lineWidth: 3
                };
                
                try {
                  annotation.config.style.setAnnotationStyles(anno.id, style);
                } catch (styleErr) {
                  console.warn(`[StackRadiologyViewer ${instanceId}] Could not set annotation style:`, styleErr);
                }
              } catch (createError) {
                console.error(`[StackRadiologyViewer ${instanceId}] Error creating annotation:`, createError);
              }
            }
          } catch (err) {
            console.error(`[StackRadiologyViewer ${instanceId}] Error highlighting annotation:`, err);
          }
          
          // Update the current slice state
          setCurrentSlice(targetSliceIndex);
          
          // Make sure viewport is triggered to render
          viewport.render();
          
          // Force a second render after a short delay to ensure annotations appear
          setTimeout(() => {
            if (mountedRef.current && renderingEngineRef.current) {
              try {
                const vp = renderingEngineRef.current.getViewport(viewportId);
                if (vp) {
                  // Re-get tool group to ensure RectangleROI tool is active
                  const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
                  if (toolGroup) {
                    // Make sure rectangle tool is enabled
                    toolGroup.setToolEnabled(RectangleROITool.toolName);
                  }
                  
                  // Apply viewport properties to force refresh
                  const props = vp.getProperties();
                  vp.setProperties({
                    ...props,
                    isComputedVOI: false
                  });
                  
                  // Force a full render
                  vp.render();
                }
              } catch (e) {
                console.warn(`[StackRadiologyViewer ${instanceId}] Error in delayed render during navigation:`, e);
              }
            }
          }, 300);
        } catch (error) {
          console.error(`[StackRadiologyViewer ${instanceId}] Error navigating to annotation:`, error);
        }
      }
    } else {
      console.warn(`Annotation navigation failed: targetId=${targetAnnotationId}, foundAnno=${!!targetAnno}, sliceIndex=${targetAnno?.sliceIndex}`);
    }
  }, [targetAnnotationId, annotations, viewportId]);

  const handleSliceChange = (evt) => {
    if (!renderingEngineRef.current) {
      console.warn("Slice change event with no rendering engine");
      return;
    }
    
    try {
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      if (viewport) {
        const currentIndex = viewport.getCurrentImageIdIndex();
        const imageIds = viewport.getImageIds();
        
        // Only update and render if the slice actually changed
        if (currentIndex !== currentSlice) {
          // Update the state to reflect the current slice
          setCurrentSlice(currentIndex);

          // If there's a new annotation in progress,
          // keep updating its slice index as user scrolls
          if (window.ACTIVE_RADIOLOGY_VIEWPORT?.instanceId === instanceId &&
              window.ACTIVE_RADIOLOGY_VIEWPORT.pendingAnnotation) {
            window.ACTIVE_RADIOLOGY_VIEWPORT.pendingAnnotation.sliceIndex = currentIndex;
          }
          
          // Verify that the current image ID exists to prevent errors
          const currentImageId = imageIds?.[currentIndex];
          if (!currentImageId) {
            console.warn(`Invalid image ID at index ${currentIndex}`);
            return;
          }
        }
      } else {
        console.warn(`Could not find viewport with ID ${viewportId}`);
      }
    } catch (e) {
      console.warn(`Error in handleSliceChange for instance ${instanceId}:`, e);
    }
  };

  // Custom scroll handler for smoother scrolling
  const handleCustomScroll = useCallback((event) => {
    event.preventDefault();
    
    if (!renderingEngineRef.current || !viewportReadyRef.current) return;
    
    try {
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      
      if (!viewport) return;
      
      const imageIds = viewport.getImageIds();
      if (!imageIds || imageIds.length === 0) return;
      
      const currentIndex = viewport.getCurrentImageIdIndex();
      const direction = Math.sign(event.deltaY);
      
      // Determine the number of slices to jump based on speed
      const delta = direction * scrollSpeedRef.current;
      
      // Calculate new index, ensuring it stays within bounds
      let newIndex = currentIndex + delta;
      newIndex = Math.max(0, Math.min(imageIds.length - 1, newIndex));
      
      // Only update if the index changed
      if (newIndex !== currentIndex) {
        // Update slice
        viewport.setImageIdIndex(newIndex);
        viewport.render();
        setCurrentSlice(newIndex);
        
        // Get VOI values for the new slice and update state
        try {
          const { voiRange } = viewport.getProperties();
          if (voiRange) {
            setVoiRange({
              windowWidth: voiRange.upper - voiRange.lower,
              windowCenter: (voiRange.upper + voiRange.lower) / 2,
            });
          }
        } catch (voiError) {
          console.warn("Error getting VOI for new slice:", voiError);
        }
        
        // Update pending annotation slice index if applicable
        if (window.ACTIVE_RADIOLOGY_VIEWPORT?.instanceId === instanceId &&
            window.ACTIVE_RADIOLOGY_VIEWPORT.pendingAnnotation) {
          window.ACTIVE_RADIOLOGY_VIEWPORT.pendingAnnotation.sliceIndex = newIndex;
        }
      }
      
      // Clear any existing timer
      if (scrollingTimerRef.current) {
        clearTimeout(scrollingTimerRef.current);
      }
      
      // Set a timer to detect fast scrolling and increase speed
      scrollingTimerRef.current = setTimeout(() => {
        setScrollSpeed(1); // Reset to default when scrolling stops
      }, 200);
      
      // Adjust speed if user is scrolling quickly
      const now = Date.now();
      if (!scrollingTimerRef.current._lastScrollTime) {
        scrollingTimerRef.current._lastScrollTime = now;
      } else if (now - scrollingTimerRef.current._lastScrollTime < 100) {
        // Fast scrolling detected, increase speed (max 5)
        setScrollSpeed(prev => Math.min(prev + 0.5, 5));
      }
      scrollingTimerRef.current._lastScrollTime = now;
      
    } catch (e) {
      console.warn("Error in custom scroll handler:", e);
    }
  }, [instanceId, viewportId]);

  // Set up handlers for VOI changes
  useEffect(() => {
    if (!cornerstone.eventTarget || !renderingEngineRef.current) {
        return;
    }

    // Function to handle VOI changes from the tool
    const handleVOIModified = (evt) => {
      if (!evt.detail || evt.detail.viewportId !== viewportId) {
        return; // Ignore events for other viewports
      }

      try {
        const viewport = renderingEngineRef.current.getViewport(viewportId);
        if (!viewport) return;

        // Get current VOI values from the viewport
        const props = viewport.getProperties();
        if (!props.voiRange) return;

        // Calculate window width and center from upper/lower bounds
        if (typeof props.voiRange.upper === 'number' && typeof props.voiRange.lower === 'number') {
          const windowWidth = props.voiRange.upper - props.voiRange.lower;
          const windowCenter = (props.voiRange.upper + props.voiRange.lower) / 2;

          // Update state
          setVoiRange({
            windowWidth,
            windowCenter
          });
        }
      } catch (error) {
        console.error(`[VOI Handler - ${instanceId}] Error handling VOI event:`, error);
      }
    };

    cornerstone.eventTarget.addEventListener(
      Events.VOI_MODIFIED,
      handleVOIModified
    );

    // Cleanup function
    return () => {
      if (cornerstone.eventTarget) {
        cornerstone.eventTarget.removeEventListener(
          Events.VOI_MODIFIED,
          handleVOIModified
        );
      } else {
        console.warn(`[VOI Listener Setup - ${instanceId}] Cannot remove listener: cornerstone.eventTarget is null/undefined during cleanup for ${viewportId}`);
      }
    };
    // Depend on viewportId and the existence of the rendering engine
  }, [viewportId, renderingEngineRef.current]);

  const initializeTools = () => {
    // Clear any existing tool group
    try {
      const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
      if (toolGroup) {
        ToolGroupManager.destroyToolGroup(toolGroupIdRef.current);
      }
    } catch (e) {
      console.warn("Error clearing existing tool group:", e);
    }

    // Get the viewer element
    if (!viewerRef.current) {
      console.warn("Cannot initialize tools: viewer element not attached");
      return;
    }

    // Create new tool group
    const toolGroup = ToolGroupManager.createToolGroup(toolGroupIdRef.current);

    if (!toolGroup) {
      throw new Error(`Failed to create tool group ${toolGroupIdRef.current}`);
    }

    // Add tools
    addTool(ZoomTool);
    addTool(PanTool);
    addTool(RectangleROITool);
    addTool(StackScrollTool); // We'll keep this registered but won't use its wheel binding
    addTool(WindowLevelTool);

    // Add tools to the tool group with better configuration
    toolGroup.addTool(ZoomTool.toolName);
    toolGroup.addTool(PanTool.toolName);
    toolGroup.addTool(RectangleROITool.toolName, {
      // Enhanced configuration for rectangle ROI tool
      configuration: {
        // Styling options for better visibility
        color: [0, 255, 0], // Green color
        lineWidth: 3,       // Thicker lines
        lineDash: [],       // Solid line
        showHandles: true,  // Show handles for better visibility
        handleRadius: 6,    // Larger handles for easier grabbing
        displayStats: false // Don't show stats by default
      }
    });
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.addTool(WindowLevelTool.toolName, {
      // Add specific configuration for window level tool
      configuration: {
        // Make sure this is enabled for STACK viewport type
        volumeId: null, // null for STACK viewport type
        orientation: null, // null for STACK viewport type
        defaultViewport: {
          voi: {
            windowWidth: 400,
            windowCenter: 40
          }
        }
      }
    });

    // Set initial active tools
    // We no longer activate the wheel binding since we're using our custom handler
    // But keep the tool registered for programmatic scrolling if needed
    
    // Check if we should prioritize annotation display
    if (showAnnotations && annotations?.length > 0) {
      // Set RectangleROI as active to ensure annotations are visible and interactive
      toolGroup.setToolActive(RectangleROITool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }], // Left mouse button
      });
      
      setActiveToolState(RectangleROITool.toolName);
      
      // Make sure windowing is still available but as secondary
      toolGroup.setToolActive(WindowLevelTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Secondary }], // Right mouse button
      });
    } else {
      // Default behavior - windowing is primary
      toolGroup.setToolActive(WindowLevelTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }], // Left mouse drag for windowing
      });
      setActiveToolState(WindowLevelTool.toolName);
    }

    // Enable the RectangleROI tool so that annotations are visible
    toolGroup.setToolEnabled(RectangleROITool.toolName);
    
    // If there are annotations to display, make sure we also set up the tool for proper display
    if (showAnnotations && annotations?.length > 0) {
      // Apply enhanced configuration
      try {
        annotation.config.style.setViewportToolStyles(viewportId, {
          annotationStyle: {
            rectangleRoi: {
              color: 'rgb(0, 255, 0)',      // Green
              colorHighlighted: 'rgb(0, 255, 255)', // Cyan when highlighted
              lineWidth: 3,
              lineDash: [],
              isVisible: true,              // Make sure visibility is explicitly set to true
              textBoxVisibility: false      // Don't show text labels by default
            }
          },
          toolGroupId: toolGroupIdRef.current
        });
      } catch (err) {
        console.warn("Error setting viewport tool styles:", err);
      }
    }
    
    toolGroup.addViewport(viewportId, renderingEngineId);

    // Listen for the annotation added event
    const eventTarget = cornerstone.eventTarget;
    if (eventTarget) {
      // Create a named handler function that we can reference for removal
      const handleAnnotationCompleted = (evt) => {
        // CRITICAL FIX: Check if this event was meant for this specific viewer
        // Check if we have an active viewport that doesn't match this instance
        if (window.ACTIVE_RADIOLOGY_VIEWPORT &&
            window.ACTIVE_RADIOLOGY_VIEWPORT.instanceId !== instanceId) {
          return;
        }
        
        // Always explicitly update annotation metadata with this viewer's identity
        // This ensures we don't get cross-contamination between viewers
        if (evt.detail?.annotation) {
          if (!evt.detail.annotation.metadata) {
            evt.detail.annotation.metadata = {};
          }
          evt.detail.annotation.metadata.viewportId = viewportId;
          evt.detail.annotation.metadata.sourceItemId = itemId;
          evt.detail.annotation.metadata.instanceId = instanceId;
        }
        
        // Check if this annotation was created in THIS viewport
        const annotationViewportId = evt.detail?.annotation?.metadata?.viewportId;
        
        // Check if the annotation has a sourceItemId and if it matches this viewer's itemId
        const annotationSourceItemId = evt.detail?.annotation?.metadata?.sourceItemId;
        
        // Also check instanceId - this is a more reliable way to filter annotations
        const annotationInstanceId = evt.detail?.annotation?.metadata?.instanceId;
        
        // First check instanceId match - this is the most reliable check
        if (annotationInstanceId && annotationInstanceId !== instanceId) {
          return; // Skip processing annotations from other instances
        }

        // Fall back to other checks if instanceId not available
        if (annotationSourceItemId && annotationSourceItemId !== itemId) {
          return; // Skip processing annotations from other items
        }

        if (annotationViewportId && annotationViewportId !== viewportId) {
          return; // Skip processing annotations from other viewports
        }
        
        // Get current slice index from viewport
        let currentSliceIndex = null;
        try {
          const viewport = renderingEngineRef.current.getViewport(viewportId);
          currentSliceIndex = viewport.getCurrentImageIdIndex();
        } catch (e) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Error getting current slice index:`, e);
          // Fall back to state variable
          currentSliceIndex = currentSlice;
        }
        
        // Call the annotation completed callback if provided
        if (typeof onAnnotationCompleted === 'function' && evt.detail?.annotation) {
          const anno = evt.detail.annotation;

          // Create a properly formatted annotation for Case.jsx in the same format as RadiologyViewer
          const formatForCase = {
            id: `rectangle-${Date.now()}`,
            name: 'Rectangle ROI',
            description: 'Added annotation',
            toolName: 'RectangleROI',
            sliceIndex: currentSliceIndex,
            points: [
              // Use the standard cornerstone point indexes for rectangle
              // points[0] = top-left, points[2] = bottom-right for a rectangle
              // This is critical to match the format in RadiologyViewer
              [anno.data?.handles?.points[0][0], anno.data?.handles?.points[0][1], currentSliceIndex],
              [anno.data?.handles?.points[3][0], anno.data?.handles?.points[3][1], currentSliceIndex],
            ],
            sourceItemId: itemId,
            viewportId: viewportId,
            instanceId: instanceId
          };
          
          onAnnotationCompleted(itemId, {...formatForCase, userRole: userRoleRef.current});
        }
      };
      
      eventTarget.addEventListener(
        Events.ANNOTATION_COMPLETED,
        handleAnnotationCompleted
      );
    }
    
    // Set up global annotation configuration to make annotations always visible
    try {
      annotation.config.style.setViewportToolStyles(viewportId, {
        annotationStyle: {
          rectangleRoi: {
            color: 'rgb(0, 255, 0)',
            colorHighlighted: 'rgb(0, 255, 255)',
            lineWidth: 3,
            lineDash: [],
            isVisible: true,
            textBoxVisibility: false
          }
        },
        toolGroupId: toolGroupIdRef.current
      });
    } catch (error) {
      console.warn(`[StackRadiologyViewer ${instanceId}] Error setting annotation styles:`, error);
    }

    return toolGroupIdRef.current;
  };

  const loadMetadata = async () => {
    const baseUrl = `${window.location.origin}${process.env.PUBLIC_URL || ""}`;
    const { files, blobUrls } = await loadDicomZip(seriesName, baseUrl, handleLoadProgress, zipUrl);

    const entries = [];
    const metadata = new Map();
    let firstFoRUID = null;

    for (const [filename, arrayBuffer] of files.entries()) {
      try {
        const byteArray = new Uint8Array(arrayBuffer);
        const dataSet = dicomParser.parseDicom(byteArray);

        const imageId = `wadouri:${blobUrls.get(filename)}`;
        const foRUID = dataSet.string("x00200052");
        const instanceNumber = dataSet.intString("x00200013") || 0;
        const sliceLocation = dataSet.floatString("x00201041") || 0;

        if (!firstFoRUID && foRUID) {
          firstFoRUID = foRUID;
        } else if (foRUID && firstFoRUID && foRUID !== firstFoRUID) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Inconsistent FrameOfReferenceUID found in series (${foRUID} vs ${firstFoRUID})`);
        }

        entries.push({
          imageId,
          instanceNumber,
          sliceLocation,
          meta: {
            imageOrientationPatient:
              dataSet.string("x00200037")?.split("\\").map(parseFloat) || [1, 0, 0, 0, 1, 0],
            imagePositionPatient:
              dataSet.string("x00200032")?.split("\\").map(parseFloat) || [0, 0, 0],
            pixelSpacing:
              dataSet.string("x00280030")?.split("\\").map(parseFloat) || [1.0, 1.0],
            rows: dataSet.uint16("x00280010") || 512,
            columns: dataSet.uint16("x00280011") || 512,
            pixelRepresentation: dataSet.uint16("x00280103") || 0,
            bitsAllocated: dataSet.uint16("x00280100") || 16,
            bitsStored: dataSet.uint16("x00280101") || 12,
            highBit: dataSet.uint16("x00280102") || 11,
            photometricInterpretation:
              dataSet.string("x00280004") || "MONOCHROME2",
            windowWidth: dataSet.floatString("x00281051"),
            windowCenter: dataSet.floatString("x00281050"),
            frameOfReferenceUID: foRUID,
            sliceLocation,
            instanceNumber,
          },
        });
      } catch (error) {
        console.error(`Error loading metadata for file ${filename}:`, error);
      }
    }

    entries.sort((a, b) => {
      if (a.instanceNumber !== b.instanceNumber) return a.instanceNumber - b.instanceNumber;
      return a.sliceLocation - b.sliceLocation;
    });

    const imageIds = entries.map((e) => {
      metadata.set(e.imageId, e.meta);
      return e.imageId;
    });

    if (imageIds.length === 0) {
      throw new Error(`Could not load or parse any valid DICOM files for series ${seriesName}`);
    }

    metadataRef.current = metadata;
    totalSlicesRef.current = imageIds.length;
    frameOfReferenceUIDRef.current = firstFoRUID || `for-${seriesName}`;

    return imageIds;
  };

  const setupViewer = async (imageIds) => {
    if (!viewerRef.current || !mountedRef.current) {
      // Bailing out here used to leave the loading overlay spinning forever:
      // setLoading(false) only runs in the try/finally below. Surface it instead.
      if (mountedRef.current) {
        console.warn(`[StackRadiologyViewer ${instanceId}] Viewer element not attached; aborting setup`);
        setError("Le viewer n'a pas pu s'initialiser. Rechargez la page.");
        setLoading(false);
      }
      return;
    }

    // Register the metadata provider
    const metadataProvider = (type, imageId) => {
      if (!metadataRef.current.has(imageId)) {
        // Instead of warning, return default values for common metadata types
        
        // Provide default metadata to prevent errors
        if (type === 'imagePlaneModule') {
          // Get the slice index from the imageId if possible
          let sliceIndex = 0;
          try {
            // Extract index from imageId (assuming format like "dicomweb:path/to/file/1.dcm")
            const parts = imageId.split('/');
            const filename = parts[parts.length - 1];
            const potentialIndex = parseInt(filename.split('.')[0], 10);
            if (!isNaN(potentialIndex)) {
              sliceIndex = potentialIndex;
            }
          } catch (e) {
            // Could not extract slice index from imageId, using default
          }
          
          return {
            imageOrientationPatient: [1, 0, 0, 0, 1, 0],
            // Use slice index as Z coordinate when no metadata is available
            imagePositionPatient: [0, 0, sliceIndex || 0],
            pixelSpacing: [1, 1],
            rows: 512,
            columns: 512,
            frameOfReferenceUID: frameOfReferenceUIDRef.current || 'unknown-frame-of-reference',
          };
        } else if (type === 'voiLutModule') {
          return {
            windowWidth: 400,
            windowCenter: 40,
          };
        } else if (type === "imagePixelModule") {
          return {
            bitsAllocated: 16,
            bitsStored: 12,
            highBit: 11,
            pixelRepresentation: 0, // unsigned
            photometricInterpretation: "MONOCHROME2",
            rows: 512,
            columns: 512,
          };
        } else if (type === "scalingModule") {
          // Default scaling parameters
          return {
            rescaleSlope: 1.0,
            rescaleIntercept: 0.0,
            suvbw: 1.0,
          };
        } else if (type === "calibrationModule") {
          // Default calibration data
          return {
            pixelSpacing: [1, 1],
            rowPixelSpacing: 1,
            columnPixelSpacing: 1,
          };
        }
        
        return undefined;
      }
      
      const imageMeta = metadataRef.current.get(imageId);
      
      if (type === 'imagePlaneModule') {
        // Ensure imagePositionPatient has a valid Z value
        let imagePositionPatient = imageMeta.imagePositionPatient || [0, 0, 0];
        
        // Only use slice index as Z coordinate if the Z value is not present in DICOM
        if (imagePositionPatient[2] === undefined || imagePositionPatient[2] === null) {
          const sliceIndex = imageIds.indexOf(imageId);
          imagePositionPatient[2] = sliceIndex;
        }
        
        return {
          imageOrientationPatient: imageMeta.imageOrientationPatient || [1, 0, 0, 0, 1, 0],
          imagePositionPatient: imagePositionPatient,
          pixelSpacing: imageMeta.pixelSpacing || [1, 1],
          rows: imageMeta.rows || 512,
          columns: imageMeta.columns || 512,
          frameOfReferenceUID: imageMeta.frameOfReferenceUID || frameOfReferenceUIDRef.current,
        };
      } else if (type === 'voiLutModule') {
        return {
          windowWidth: imageMeta.windowWidth || 400,
          windowCenter: imageMeta.windowCenter || 40,
        };
      } else if (type === "imagePixelModule") {
        return {
          bitsAllocated: imageMeta.bitsAllocated || 16,
          bitsStored: imageMeta.bitsStored || 12,
          highBit: imageMeta.highBit || 11,
          pixelRepresentation: imageMeta.pixelRepresentation || 0,
          photometricInterpretation: imageMeta.photometricInterpretation || "MONOCHROME2",
          rows: imageMeta.rows || 512,
          columns: imageMeta.columns || 512,
        };
      } else if (type === "scalingModule") {
        // Add support for scaling module
        return {
          rescaleSlope: imageMeta.rescaleSlope || 1.0,
          rescaleIntercept: imageMeta.rescaleIntercept || 0.0,
          suvbw: imageMeta.suvbw || 1.0,
        };
      } else if (type === "calibrationModule") {
        // Add support for calibration module
        return {
          pixelSpacing: imageMeta.pixelSpacing || [1, 1],
          rowPixelSpacing: (imageMeta.pixelSpacing && imageMeta.pixelSpacing[1]) || 1,
          columnPixelSpacing: (imageMeta.pixelSpacing && imageMeta.pixelSpacing[0]) || 1,
        };
      }

      return undefined;
    };
    cornerstone.metaData.addProvider(metadataProvider, instanceId);

    try {
      // Create a new rendering engine
      const renderingEngine = acquireRenderingEngine();
      renderingEngineRef.current = renderingEngine;

      // Create a viewport in the rendering engine
      const viewportInput = {
        viewportId,
        element: viewerRef.current,
        type: cornerstone.Enums.ViewportType.STACK,
        defaultOptions: {
          background: [0, 0, 0],
        },
      };
      
      await renderingEngineRef.current.enableElement(viewportInput);
      
      // Get the viewport
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      
      if (!viewport) {
        throw new Error(`Failed to create viewport ${viewportId}`);
      }
      
      viewportReadyRef.current = true;

      try {
        // Set the stack of images with explicit await
        await viewport.setStack(imageIds);
      } catch (stackError) {
        console.error("Failed to set stack:", stackError);
        throw stackError;
      }

      // Set initial VOI if available from first image
      let initialVOISet = false;

      // Define a function for applying VOI settings
      const applyVOISettings = (width, center) => {
        try {
          viewport.setVOI(width, center);

          viewport.setProperties({
            voiRange: {
              windowWidth: width,
              windowCenter: center,
            },
            interpolationType: cornerstone.Enums.InterpolationType.LINEAR
          });

          setVoiRange({
            windowWidth: width,
            windowCenter: center,
          });
          return true;
        } catch (voiError) {
          console.warn("[VOI] Failed to apply VOI settings:", voiError);
          return false;
        }
      };

      if (metadataRef.current.has(imageIds[0])) {
        const firstImageMeta = metadataRef.current.get(imageIds[0]);

        if (firstImageMeta.windowWidth && firstImageMeta.windowCenter) {
          // Ensure the VOI values are reasonable
          const windowWidth = firstImageMeta.windowWidth > 0 && firstImageMeta.windowWidth < 4000
              ? firstImageMeta.windowWidth
              : 400;
          const windowCenter = firstImageMeta.windowCenter > -1000 && firstImageMeta.windowCenter < 1000
              ? firstImageMeta.windowCenter
              : 40;

          initialVOISet = applyVOISettings(windowWidth, windowCenter);
        }
      }

      // If we couldn't set VOI from metadata, use standard defaults
      if (!initialVOISet) {
        initialVOISet = applyVOISettings(400, 40);

        // If default settings didn't work, try other common DICOM window presets
        if (!initialVOISet) {
          initialVOISet = applyVOISettings(80, 40);
        }

        if (!initialVOISet) {
          initialVOISet = applyVOISettings(1500, -600);
        }

        if (!initialVOISet) {
          initialVOISet = applyVOISettings(2500, 480);
        }
      }

      // Set the initial image to display - start with the middle slice
      const initialSliceIndex = Math.floor(imageIds.length / 2);
      try {
        // Check if we have a target annotation first - if so, use its slice index instead
        if (targetAnnotationId && annotations.length > 0) {
          const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);
          if (targetAnno && typeof targetAnno.sliceIndex === 'number' &&
              targetAnno.sliceIndex >= 0 && targetAnno.sliceIndex < imageIds.length) {
            viewport.setImageIdIndex(targetAnno.sliceIndex);
            setCurrentSlice(targetAnno.sliceIndex);
          } else {
            viewport.setImageIdIndex(initialSliceIndex);
            setCurrentSlice(initialSliceIndex);
          }
        } else {
          viewport.setImageIdIndex(initialSliceIndex);
          setCurrentSlice(initialSliceIndex);
        }
      } catch (sliceError) {
        console.warn("Failed to set initial slice:", sliceError);
      }

      // Reset the camera and render
      viewport.resetCamera();
      viewport.render();
      
      // Initialize the Window/Level sync manager
      if (renderingEngineRef.current && viewportReadyRef.current) {
        windowLevelSyncRef.current = new WindowLevelSyncManager(
          renderingEngineRef.current,
          toolGroupIdRef.current,
          handleVOIUpdate // Pass the callback
        );

        // Enable window/level synchronization for STACK viewport
        windowLevelSyncRef.current.enable([viewportId], true);
      } else {
        console.warn("[WindowLevelSync] Cannot initialize: rendering engine or viewport not ready");
      }

      // Initialize tools - the function already uses toolGroupIdRef.current
      initializeTools();

      // If we have annotations, explicitly force RectangleROI to be active
      if (showAnnotations && annotations?.length > 0) {
        try {
          const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
          if (toolGroup) {
            // Make Rectangle tool active with more visible styling
            toolGroup.setToolConfiguration(RectangleROITool.toolName, {
              color: [255, 0, 0],        // Bright red
              lineWidth: 4,              // Thicker line
              lineDash: [],              // Solid line
              showHandles: true,
              handleRadius: 6,
              displayStats: false,
            });
            
            // Make sure it's enabled and active
            toolGroup.setToolEnabled(RectangleROITool.toolName);
            
            // Force tool active state in the UI
            setActiveToolState(RectangleROITool.toolName);
          }
        } catch (e) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Error activating RectangleROI tool: ${e.message}`);
        }
      }

      // Add event listeners
      viewport.element.addEventListener("wheel", handleCustomScroll, { passive: false });

      // Load annotations if provided
      if (showAnnotations && annotations.length > 0) {
        try {
          for (const anno of annotations) {
            // Skip processing if annotation doesn't have points or has invalid data
            if (!anno.points || anno.points.length < 2) {
              console.warn(`[StackRadiologyViewer ${instanceId}] Missing or invalid points for annotation ${anno.id}`);
              return;
            }
            
            // Find the correct slice index for this annotation
            let sliceIndex = anno.sliceIndex;
            if (sliceIndex === undefined || sliceIndex === null) {
              console.warn(`[StackRadiologyViewer ${instanceId}] Missing sliceIndex for annotation ${anno.id}`);
              
              // Try to get sliceIndex from the first point's Z coordinate
              if (anno.points[0][2] !== undefined) {
                sliceIndex = anno.points[0][2];
              } else {
                console.warn(`[StackRadiologyViewer ${instanceId}] Cannot determine slice for annotation ${anno.id}, skipping`);
                return;
              }
            }
            
            // Ensure sliceIndex is within bounds
            if (sliceIndex < 0 || sliceIndex >= imageIds.length) {
              console.warn(`[StackRadiologyViewer ${instanceId}] Annotation ${anno.id} has out-of-bounds sliceIndex ${sliceIndex} (max: ${imageIds.length-1})`);
              if (sliceIndex < 0) sliceIndex = 0;
              if (sliceIndex >= imageIds.length) sliceIndex = imageIds.length - 1;
            }
            
            // Get the image ID for this slice
            const imageId = imageIds[sliceIndex];
            
            // CRITICAL FIX: Use proper Z coordinate for the annotation
            let worldZ = getWorldZ(sliceIndex, true);

            // Format points for cornerstone with the determined Z coordinate
            let formattedPoints = [];
            
            if (anno.points.length === 2) {
              // Extract points
              const topLeft = anno.points[1];
              const bottomRight = anno.points[0];
              
              // Get coordinates
              const x1 = topLeft[0];
              const y1 = topLeft[1];
              const x2 = bottomRight[0];
              const y2 = bottomRight[1];
              
              // Get proper Z coordinate using our helper function
              const worldZ = getWorldZ(sliceIndex, true);

              // Create rectangle corners with the corrected Z coordinate
              formattedPoints = [
                [x1, y1, worldZ],  // top-left
                [x2, y1, worldZ],  // top-right
                [x2, y2, worldZ],  // bottom-right
                [x1, y2, worldZ]   // bottom-left
              ];
            } else if (anno.points.length === 4) {
              // Get proper Z coordinate using our helper function
              const worldZ = getWorldZ(sliceIndex, true);

              // Reuse X, Y but update Z to the correct value
              formattedPoints = anno.points.map(p => [p[0], p[1], worldZ]);
            } else {
              console.warn(`[StackRadiologyViewer ${instanceId}] Unknown point format with ${anno.points.length} points`);
              return;
            }
            
            // Generate a persistent frame of reference UID for annotations
            // const frameOfReferenceUID = `stack-${seriesName}-${instanceId}-FOR`; // REMOVED - Use actual UID

            // Create the cornerstone annotation object - CRITICAL for visibility in stack viewports
            const cornerstoneAnnotation = {
              annotationUID: anno.id,
              metadata: {
                toolName: 'RectangleROI',
                viewportId: viewportId,
                sourceItemId: itemId,
                instanceId: instanceId,
                FrameOfReferenceUID: frameOfReferenceUIDRef.current, // USE REF VALUE
                referencedImageId: imageId
              },
              data: {
                handles: {
                  points: formattedPoints,
                  activeHandleIndex: null,
                  textBox: {
                    hasMoved: false,
                    worldPosition: [formattedPoints[0][0], formattedPoints[0][1], formattedPoints[0][2]], // Ensure correct Z
                    worldBoundingBox: {
                      topLeft: [formattedPoints[0][0], formattedPoints[0][1], formattedPoints[0][2]], // Ensure correct Z
                      topRight: [formattedPoints[1][0], formattedPoints[1][1], formattedPoints[1][2]], // Ensure correct Z
                      bottomLeft: [formattedPoints[3][0], formattedPoints[3][1], formattedPoints[3][2]], // Ensure correct Z
                      bottomRight: [formattedPoints[2][0], formattedPoints[2][1], formattedPoints[2][2]] // Ensure correct Z
                    }
                  }
                },
                label: anno.name || '',
                cachedStats: {},
                visible: true, // Explicitly set visibility
                color: [255, 0, 0], // Bright red for high visibility
                lineWidth: 2
              },
              highlighted: anno.id === targetAnnotationId,
              isLocked: false,
              isVisible: true // Explicit visibility
            };
            
            // Add to cornerstone state with properly scoped annotation group
            const annotationGroupSelector = {
              viewportUID: viewportId,
              frameOfReferenceUID: frameOfReferenceUIDRef.current // USE REF VALUE
            };
            
            // Add to cornerstone state
            annotation.state.addAnnotation(cornerstoneAnnotation, annotationGroupSelector);
            
            // CRITICAL: Explicitly set annotation visibility
            annotation.visibility.setAnnotationVisibility(anno.id, true);
            
            // Apply distinct style for better visibility - with more extreme settings
            const style = {
              color: 'rgb(0, 255, 0)',              // Bright red
              colorHighlighted: 'rgb(0, 255, 0)',  // Magenta when highlighted
              lineWidth: 2,
              lineDash: [],
              isVisible: true,
              textBoxVisibility: false, // Hide text box for cleaner appearance
              handleRadius: 6
            };
            
            try {
              annotation.config.style.setAnnotationStyles(anno.id, style);
            } catch (styleErr) {
              console.warn(`[StackRadiologyViewer ${instanceId}] Could not set annotation style:`, styleErr);
            }
            
          }
        } catch (annoError) {
          console.warn("Error loading annotations:", annoError);
        }
        
        viewport.render();
      }

      // Set global annotation style with more extreme settings
      annotation.config.style.setViewportToolStyles(viewportId, {
        annotationStyle: {
          rectangleRoi: {
            color: 'rgb(0, 255, 0)',
            colorHighlighted: 'rgb(0, 255, 0)',
            lineWidth: 2,
            lineDash: [],
            isVisible: true,
            textBoxVisibility: false,
            handleRadius: 6
          }
        },
        toolGroupId: toolGroupIdRef.current
      });

      // Navigate to the targeted annotation if specified
      if (targetAnnotationId) {
        const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);
        if (targetAnno && typeof targetAnno.sliceIndex === 'number') {
          viewport.setImageIdIndex(targetAnno.sliceIndex);
          setCurrentSlice(targetAnno.sliceIndex);

          // Force a render
          viewport.render();
          
          // Add a delayed render to ensure annotation is visible
          setTimeout(() => {
            if (mountedRef.current && renderingEngineRef.current) {
              try {
                renderingEngineRef.current.renderViewport(viewportId);
              } catch (e) {
                console.warn(`[StackRadiologyViewer ${instanceId}] Error in delayed render:`, e);
              }
            }
          }, 100);
        }
      }
      
      // Final render to show annotations
      viewport.render();
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] Error setting up viewer:`, error);
      setError('Failed to set up viewer: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToolClick = (toolName) => {
    if (!renderingEngineRef.current || !viewportReadyRef.current) return;

    const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
    if (!toolGroup) {
      console.error("Tool group not found");
      return;
    }
  
    const resetCursor = () => {
      if (viewerRef.current) {
        viewerRef.current.style.cursor = "default";
      }
    };
  
    // First, set all tools to disabled state
    toolGroup.setToolDisabled(PanTool.toolName);
    toolGroup.setToolDisabled(ZoomTool.toolName);
    toolGroup.setToolEnabled(RectangleROITool.toolName);
    toolGroup.setToolDisabled(WindowLevelTool.toolName);
    // No longer set StackScrollTool active here, we use our custom handler

    // Set the active tool based on the tool name
    if (toolName === PanTool.toolName) {
      if (activeTool === PanTool.toolName) {
        // Toggle off if already active
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(WindowLevelTool.toolName);
        resetCursor();
      } else {
        toolGroup.setToolActive(PanTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(PanTool.toolName);
        if (viewerRef.current) {
          viewerRef.current.style.cursor = "move";
        }
      }
    } else if (toolName === ZoomTool.toolName) {
      if (activeTool === ZoomTool.toolName) {
        // Toggle off if already active
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(WindowLevelTool.toolName);
        resetCursor();
      } else {
        toolGroup.setToolActive(ZoomTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(ZoomTool.toolName);
        if (viewerRef.current) {
          viewerRef.current.style.cursor = "zoom-in";
        }
      }
    } else if (toolName === WindowLevelTool.toolName) {
      if (activeTool !== WindowLevelTool.toolName) {
        // Make window/level active and ensure it's configured correctly
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        
        // Explicitly set this as the active tool
        setActiveToolState(WindowLevelTool.toolName);
        resetCursor();
        
        // Force update viewport to ensure window/level tool is ready
        try {
          const viewport = renderingEngineRef.current?.getViewport(viewportId);
          if (viewport) {
            viewport.render();
          }
        } catch (e) {
          console.warn("Error rendering after tool change:", e);
        }
      }
    } else if (toolName === RectangleROITool.toolName) {
      if (activeTool === RectangleROITool.toolName) {
        // Toggle off if already active
        toolGroup.setToolActive(WindowLevelTool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(WindowLevelTool.toolName);
        resetCursor();
      } else {
        toolGroup.setToolActive(RectangleROITool.toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
        setActiveToolState(RectangleROITool.toolName);
        if (viewerRef.current) {
          viewerRef.current.style.cursor = "crosshair";
        }
      }
    }
  };

  const cleanup = async () => {
    try {
      viewportReadyRef.current = false;
      
      // Clean up WindowLevelSyncManager if it exists
      if (windowLevelSyncRef.current) {
        windowLevelSyncRef.current.cleanup();
        windowLevelSyncRef.current = null;
      }
      
      // Remove event listeners
      if (viewerRef.current) {
        viewerRef.current.removeEventListener("wheel", handleCustomScroll);
        viewerRef.current.removeEventListener(Events.MOUSE_WHEEL, handleSliceChange);
      }
      
      // Clear any pending scroll timers
      if (scrollingTimerRef.current) {
        clearTimeout(scrollingTimerRef.current);
        scrollingTimerRef.current = null;
      }
      
      // Clean up the tool group if it exists
      const toolGroupId = toolGroupIdRef.current;
      if (toolGroupId) {
        try {
          const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
          if (toolGroup) {
            // Remove tools from the toolGroup
            toolGroup.removeViewport(viewportId);

            // Destroy the tool group if there are no viewports left
            if (toolGroup.getViewportIds().length === 0) {
              ToolGroupManager.destroyToolGroup(toolGroupId);
            }
          }
        } catch (e) {
          console.warn(`Error cleaning up tool group for instance ${instanceId}:`, e);
        }
      }
      
      // Clean up rendering engine
      if (renderingEngineRef.current) {
        try {
          const viewport = renderingEngineRef.current.getViewport(viewportId);
          if (viewport) {
            viewport.reset();
            viewport.dispose();
          }
        } catch (e) {
          console.warn(`Error cleaning up viewport ${viewportId}:`, e);
        }
        
        try {
          renderingEngineRef.current.disableElement(viewportId);
        } catch (e) {
          console.warn(`Error disabling element ${viewportId}:`, e);
        }
        
        try {
          releaseRenderingEngine();
        } catch (e) {
          console.warn(`Error releasing shared rendering engine:`, e);
        }

        renderingEngineRef.current = null;
      }
      
      // Remove instance-specific metadata provider
      try {
        cornerstone.metaData.removeProvider(instanceId);
      } catch (e) {
        console.warn(`Error removing metadata provider for instance ${instanceId}:`, e);
      }
      
      // Global cleanup if this is the last instance
      if (activeInstances.size === 1 && activeInstances.has(instanceId)) {
        // Only reset global state if this is the last instance
        globalInitPromise = null;
        try {
          cornerstone.cache.purgeCache();
        } catch (e) {
          console.warn('Error purging global cache:', e);
        }
        revokeDicomZipCache(seriesName, `${window.location.origin}${process.env.PUBLIC_URL || ""}`, zipUrl);
      }
    } catch (err) {
      console.warn(`Cleanup error for instance ${instanceId}:`, err);
    }
  };

  const handleResize = useCallback(() => {
    if (!renderingEngineRef.current || !viewerRef.current || !viewportReadyRef.current) {
      return;
    }

    try {
      renderingEngineRef.current.resize();
      renderingEngineRef.current.render();
    } catch (error) {
      console.warn(`Error resizing viewport: ${error.message}`);
    }
  }, []);

  // Initialize and setup on mount
  useEffect(() => {
    mountedRef.current = true;
    activeInstances.add(instanceId);
    
    // When the mouse enters this viewport, set it as the active one
    const handleMouseEnter = () => {
      window.ACTIVE_RADIOLOGY_VIEWPORT = {
        instanceId,
        itemId,
        pendingAnnotation: null
      };
    };
    
    if (viewerRef.current) {
      viewerRef.current.addEventListener('mouseenter', handleMouseEnter);
    }
    
    // Setup async
    const setup = async () => {
      try {
        setLoading(true);
        await initializeCornerstone();
        
        // Load metadata and image IDs
        const imageIds = await loadMetadata();
        
        // Set up the viewer with the loaded data
        await setupViewer(imageIds);
      } catch (err) {
        console.error(`Error setting up StackRadiologyViewer ${instanceId}:`, err);
        setError(`Failed to initialize viewer: ${err.message}`);
        setLoading(false);
      }
    };

    setup();

    return () => {
      mountedRef.current = false;
      activeInstances.delete(instanceId);
      
      if (viewerRef.current) {
        viewerRef.current.removeEventListener('mouseenter', handleMouseEnter);
      }
      
      cleanup();
    };
  }, [instanceId, itemId, seriesName]);

  // Add resize observer
  useEffect(() => {
    if (!viewerRef.current) {
      console.warn(`[ResizeObserver] viewerRef.current is not attached for ${instanceId}`);
      return;
    }

    let resizeTimeout;
    const observer = new ResizeObserver(() => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        handleResize();
      }, 250);
    });

    observer.observe(viewerRef.current);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      observer.disconnect();
    };
  }, [handleResize]);

  // Add helper method to trigger a windowing adjustment
  const triggerWindowingUpdate = useCallback(() => {
    if (!renderingEngineRef.current || !viewportReadyRef.current) return;
    
    try {
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      if (!viewport) return;
      
      const currentVOI = viewport.getProperties().voiRange;

      // Make a small adjustment to force redraw
      viewport.setVOI(currentVOI.windowWidth + 1, currentVOI.windowCenter);
      viewport.render();
      
      // Revert to original
      setTimeout(() => {
        if (mountedRef.current && renderingEngineRef.current) {
          try {
            const vp = renderingEngineRef.current.getViewport(viewportId);
            if (vp) {
              vp.setVOI(currentVOI.windowWidth, currentVOI.windowCenter);
              vp.render();
            }
          } catch (e) {
            console.warn("[TriggerWindowing] Error:", e);
          }
        }
      }, 100);
    } catch (e) {
      console.warn("[TriggerWindowing] Error triggering windowing update:", e);
    }
  }, [viewportId]);

  // Add effect to force windowing update after component mounts
  useEffect(() => {
    if (viewportReadyRef.current) {
      const timer = setTimeout(() => {
        triggerWindowingUpdate();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [triggerWindowingUpdate, viewportReadyRef.current]);

  // Helper method for VOI updates from WindowLevelSyncManager
  const handleVOIUpdate = (viewportId, voiRange) => {
    // Update state with the VOI values
    setVoiRange({
      windowWidth: voiRange.windowWidth,
      windowCenter: voiRange.windowCenter,
    });
    
    // If the rendering engine is ready, update the viewport
    if (renderingEngineRef.current && viewportReadyRef.current) {
      try {
        const viewport = renderingEngineRef.current.getViewport(viewportId);
        if (viewport) {
          // For STACK viewports, setVOI might need to be called with specific params
          // or through setProperties for better compatibility
          viewport.setProperties({
            voiRange: {
              lower: voiRange.windowCenter - (voiRange.windowWidth / 2),
              upper: voiRange.windowCenter + (voiRange.windowWidth / 2),
              windowWidth: voiRange.windowWidth,
              windowCenter: voiRange.windowCenter
            }
          });
          viewport.render();
        }
      } catch (error) {
        console.warn(`[StackRadiologyViewer - ${instanceId}] Error applying VOI update:`, error);
      }
    }
  };

  // Process and add annotations from props to Cornerstone state
  useEffect(() => {
    if (!viewportReadyRef.current || !renderingEngineRef.current || !annotations || !annotations.length) return;

    // Get the viewport
    const viewport = renderingEngineRef.current.getViewport(viewportId);
    if (!viewport) {
      console.warn(`[StackRadiologyViewer ${instanceId}] Viewport not available for annotation processing`);
      return;
    }
    
    // Get the image IDs for this viewport
    const imageIds = viewport.getImageIds();
    if (!imageIds || !imageIds.length) {
      console.warn(`[StackRadiologyViewer ${instanceId}] No image IDs available for annotation processing`);
      return;
    }
    
    try {
      

      // Configure the RectangleROI tool properly for stack viewports
      try {
        const toolGroup = ToolGroupManager.getToolGroup(toolGroupIdRef.current);
        if (toolGroup) {
          // Add the tool with specific configuration for visibility
          toolGroup.addTool(RectangleROITool.toolName, {
            configuration: {
              shadow: true,
              preventHandleOutsideImage: false,
              displayStatistics: false, // Important for visibility
              alwaysRenderOutline: true // Make sure outline is always visible
            }
          });
          
          // Set tool active
          toolGroup.setToolActive(RectangleROITool.toolName, {
            bindings: [{ mouseButton: MouseBindings.Primary }],
          });
          
          // Set active tool state in component
          setActiveToolState(RectangleROITool.toolName);
          
        }
      } catch (toolErr) {
        console.warn(`[StackRadiologyViewer ${instanceId}] Error configuring RectangleROI tool:`, toolErr);
      }

      // Process each annotation
      annotations.forEach(anno => {
        // Skip processing if annotation doesn't have valid data
        if (!anno.points || anno.points.length < 2) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Missing points for annotation ${anno.id}`);
          return;
        }
        
        // Get the slice index from the annotation
        let sliceIndex = anno.sliceIndex;
        
        // If sliceIndex is missing or invalid, try to get it from the points
        if (sliceIndex === undefined || sliceIndex === null || sliceIndex < 0 || sliceIndex >= imageIds.length) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Invalid slice index ${sliceIndex} for annotation ${anno.id}`);
          
          // Try to get sliceIndex from point Z coordinate
          if (anno.points[0] && anno.points[0][2] !== undefined) {
            sliceIndex = anno.points[0][2];
          } else {
            console.warn(`[StackRadiologyViewer ${instanceId}] Cannot determine slice for annotation ${anno.id}, using slice 0`);
            sliceIndex = 0;
          }
        }
        
        // Make sure slice index is within bounds
        if (sliceIndex < 0) sliceIndex = 0;
        if (sliceIndex >= imageIds.length) sliceIndex = imageIds.length - 1;
        
        // Get the image ID for this slice
        const imageId = imageIds[sliceIndex];
        
        // CRITICAL FIX: Use proper Z coordinate for the annotation
        let worldZ = getWorldZ(sliceIndex, true);
        
        // Try to get the correct world Z coordinate
        try {
          const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId);
          if (imagePlaneModule && imagePlaneModule.imagePositionPatient) {
            worldZ = imagePlaneModule.imagePositionPatient[2];
            
            // IMPORTANT: If the imagePositionPatient Z value is 0 or undefined, we know something is wrong
            // Force use of sliceIndex in that case
            if (worldZ === 0 && sliceIndex > 0) {
              worldZ = sliceIndex;
            }
          } else {
            // No imagePlaneModule, using sliceIndex as Z
          }
        } catch (error) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Error getting imagePositionPatient:`, error);
          // Fallback to slice index on error
          worldZ = sliceIndex;
        }
        
        // Double check: Never let worldZ be 0 if the slice index is not 0
        if (worldZ === 0 && sliceIndex > 0) {
          worldZ = sliceIndex;
        }
        
        // Format points for cornerstone with the determined Z coordinate
        let formattedPoints = [];
        
        if (anno.points.length === 2) {
          // Extract points
          const topLeft = anno.points[0];
          const bottomRight = anno.points[1];
          
          // Get coordinates
          const x1 = topLeft[0];
          const y1 = topLeft[1];
          const x2 = bottomRight[0];
          const y2 = bottomRight[1];
          
          // Get proper Z coordinate using our helper function
          const worldZ = getWorldZ(sliceIndex, true);

          // Create rectangle corners with the corrected Z coordinate
          formattedPoints = [
            [x1, y1, worldZ],  // top-left
            [x2, y1, worldZ],  // top-right
            [x2, y2, worldZ],  // bottom-right
            [x1, y2, worldZ]   // bottom-left
          ];
        } else if (anno.points.length === 4) {
          // Get proper Z coordinate using our helper function
          const worldZ = getWorldZ(sliceIndex, true);

          // Reuse X, Y but update Z to the correct value
          formattedPoints = anno.points.map(p => [p[0], p[1], worldZ]);
        } else {
          console.warn(`[StackRadiologyViewer ${instanceId}] Unknown point format with ${anno.points.length} points`);
          return;
        }
        
        // Generate a persistent frame of reference UID for annotations
        // const frameOfReferenceUID = `stack-${seriesName}-${instanceId}-FOR`; // REMOVED - Use actual UID

        // Create the cornerstone annotation object - CRITICAL for visibility in stack viewports
        const cornerstoneAnnotation = {
          annotationUID: anno.id,
          metadata: {
            toolName: 'RectangleROI',
            viewportId: viewportId,
            sourceItemId: itemId,
            instanceId: instanceId,
            FrameOfReferenceUID: frameOfReferenceUIDRef.current, // USE REF VALUE
            referencedImageId: imageId
          },
          data: {
            handles: {
              points: formattedPoints,
              activeHandleIndex: null,
              textBox: {
                hasMoved: false,
                worldPosition: [formattedPoints[0][0], formattedPoints[0][1], formattedPoints[0][2]], // Ensure correct Z
                worldBoundingBox: {
                  topLeft: [formattedPoints[0][0], formattedPoints[0][1], formattedPoints[0][2]], // Ensure correct Z
                  topRight: [formattedPoints[1][0], formattedPoints[1][1], formattedPoints[1][2]], // Ensure correct Z
                  bottomLeft: [formattedPoints[3][0], formattedPoints[3][1], formattedPoints[3][2]], // Ensure correct Z
                  bottomRight: [formattedPoints[2][0], formattedPoints[2][1], formattedPoints[2][2]] // Ensure correct Z
                }
              }
            },
            label: anno.name || '',
            cachedStats: {},
            visible: true, // Explicitly set visibility
            color: [255, 0, 0], // Bright red for high visibility
            lineWidth: 2
          },
          highlighted: anno.id === targetAnnotationId,
          isLocked: false,
          isVisible: true // Explicit visibility
        };
        
        // Add to cornerstone state with properly scoped annotation group
        const annotationGroupSelector = {
          viewportUID: viewportId,
          frameOfReferenceUID: frameOfReferenceUIDRef.current // USE REF VALUE
        };
        
        // Add to cornerstone state
        annotation.state.addAnnotation(cornerstoneAnnotation, annotationGroupSelector);
        
        // CRITICAL: Explicitly set annotation visibility
        annotation.visibility.setAnnotationVisibility(anno.id, true);
        
        // Apply distinct style for better visibility - with more extreme settings
        const style = {
          color: 'rgb(0, 255, 0)',              // Bright red
          colorHighlighted: 'rgb(0, 255, 0)',  // Magenta when highlighted
          lineWidth: 2,
          lineDash: [],
          isVisible: true,
          textBoxVisibility: false, // Hide text box for cleaner appearance
          handleRadius: 6
        };
        
        try {
          annotation.config.style.setAnnotationStyles(anno.id, style);
        } catch (styleErr) {
          console.warn(`[StackRadiologyViewer ${instanceId}] Could not set annotation style:`, styleErr);
        }
        
      });

      // Set global annotation style with more extreme settings
      annotation.config.style.setViewportToolStyles(viewportId, {
        annotationStyle: {
          rectangleRoi: {
            color: 'rgb(0, 255, 0)',
            colorHighlighted: 'rgb(0, 255, 0)',
            lineWidth: 2,
            lineDash: [],
            isVisible: true,
            textBoxVisibility: false,
            handleRadius: 6
          }
        },
        toolGroupId: toolGroupIdRef.current
      });

      // Navigate to the targeted annotation if specified
      if (targetAnnotationId) {
        const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);
        if (targetAnno && typeof targetAnno.sliceIndex === 'number') {
          viewport.setImageIdIndex(targetAnno.sliceIndex);
          setCurrentSlice(targetAnno.sliceIndex);

          // Force a render
          viewport.render();
          
          // Add a delayed render to ensure annotation is visible
          setTimeout(() => {
            if (mountedRef.current && renderingEngineRef.current) {
              try {
                renderingEngineRef.current.renderViewport(viewportId);
              } catch (e) {
                console.warn(`[StackRadiologyViewer ${instanceId}] Error in delayed render:`, e);
              }
            }
          }, 100);
        }
      }
      
      // Final render to show annotations
      viewport.render();
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] Error processing annotations:`, error);
    }
  }, [annotations, targetAnnotationId, instanceId, itemId, viewportId, seriesName]);
  
  // Add a new effect specifically for handling visibility when showing/hiding annotations
  useEffect(() => {
    if (!viewportReadyRef.current || !renderingEngineRef.current || !annotations.length) {
      return;
    }
    
    try {
      // Get all annotations in the state
      const allAnnotations = annotation.state.getAllAnnotations();
      
      // Collect all annotation IDs from our props
      const annotationIds = new Set(annotations.map(a => a.id));
      
      // Set visibility according to showAnnotations prop
      allAnnotations.forEach(anno => {
        if (annotationIds.has(anno.annotationUID)) {
          // Set visibility using cornerstone's method
          annotation.visibility.setAnnotationVisibility(anno.annotationUID, showAnnotations);
        }
      });
      
      // Force render after visibility change
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      if (viewport) {
        viewport.render();
      }
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] Error setting annotation visibility:`, error);
    }
  }, [showAnnotations, annotations, instanceId, viewportId]);

  // After the annotation handling effect, add this navigation effect
  // Special effect for handling target annotation navigation
  useEffect(() => {
    if (!targetAnnotationId || !viewportReadyRef.current || !renderingEngineRef.current) return;

    const targetAnno = annotations.find(anno => anno.id === targetAnnotationId);
    if (!targetAnno || typeof targetAnno.sliceIndex !== 'number') return;

    try {
      const viewport = renderingEngineRef.current.getViewport(viewportId);
      if (!viewport) return;
      
      // Navigate to the correct slice
      const imageIds = viewport.getImageIds();
      let sliceIndex = targetAnno.sliceIndex;
      
      // Ensure index is in bounds
      if (sliceIndex < 0) sliceIndex = 0;
      if (sliceIndex >= imageIds.length) sliceIndex = imageIds.length - 1;
      
      // Set slice and update state
      viewport.setImageIdIndex(sliceIndex);
      setCurrentSlice(sliceIndex);
      
      // IMPORTANT: Force a full rendering pipeline to ensure annotations are visible
      viewport.render();
      
      // Force annotation visibility and highlighting
      try {
        const allAnnos = annotation.state.getAllAnnotations();
        allAnnos.forEach(anno => {
          if (anno.annotationUID === targetAnnotationId) {
            anno.highlighted = true;
            annotation.visibility.setAnnotationVisibility(anno.annotationUID, true);
            
            // Explicitly set style for maximum visibility
            annotation.config.style.setAnnotationStyles(anno.annotationUID, {
              color: 'rgb(0, 255, 0)',            // Red
              colorHighlighted: 'rgb(0, 255, 0)', // Magenta
              lineWidth: 3,
              lineDash: [],
              isVisible: true,
              textBoxVisibility: false
            });
            
          }
        });
      } catch (annoError) {
        console.warn(`[StackRadiologyViewer ${instanceId}] Error highlighting annotation:`, annoError);
      }
      
      // Staggered renders to ensure annotation appears
      const renderDelays = [100, 300, 600];
      renderDelays.forEach(delay => {
        setTimeout(() => {
          if (mountedRef.current && renderingEngineRef.current) {
            try {
              renderingEngineRef.current.renderViewport(viewportId);
            } catch (e) {
              // Ignore render errors
            }
          }
        }, delay);
      });
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] Error navigating to annotation:`, error);
    }
  }, [targetAnnotationId, annotations, instanceId, viewportId]);

  const viewport = renderingEngineRef.current?.getViewport(viewportId);
  const imageIds = viewport?.getImageIds();

  const getWorldZ = (sliceIndex, verbose = false) => {
    if (sliceIndex === undefined || sliceIndex === null) {
      console.warn(`[StackRadiologyViewer ${instanceId}] getWorldZ called with invalid sliceIndex:`, sliceIndex);
      return 0;
    }

    // Ensure the slice index is within bounds
    const boundedSliceIndex = Math.max(0, Math.min(sliceIndex, imageIds.length - 1));
    if (boundedSliceIndex !== sliceIndex) {
      console.warn(`[StackRadiologyViewer ${instanceId}] getWorldZ received out-of-bounds sliceIndex ${sliceIndex}, using ${boundedSliceIndex} instead`);
    }

    // Get the imageId for this slice
    const imageId = imageIds[boundedSliceIndex];
    if (!imageId) {
      console.warn(`[StackRadiologyViewer ${instanceId}] getWorldZ: No imageId found for sliceIndex ${boundedSliceIndex}`);
      return 0;
    }

    try {
      // Get image plane metadata directly using cornerstone.metaData API
      const imagePlaneModule = cornerstone.metaData.get('imagePlaneModule', imageId);
      
      // Calculate worldZ with proper spacing consideration
      let worldZ = imagePlaneModule?.imagePositionPatient?.[2];
      
      // UPDATED FALLBACK: Also use sliceIndex if worldZ is 0 unless sliceIndex is also 0
      if (worldZ === undefined || worldZ === null || (worldZ === 0 && boundedSliceIndex !== 0)) {
        if (verbose) {
          console.warn(`[StackRadiologyViewer ${instanceId}] getWorldZ: Invalid Z position in imagePositionPatient for imageId ${imageId}. Using sliceIndex ${boundedSliceIndex} as fallback.`);
        }
        
        // Fallback to using slice index
        worldZ = boundedSliceIndex;
      }
      
      return worldZ;
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] getWorldZ error:`, error);
      return boundedSliceIndex;
    }
  };

  const handleScrollbarChange = useCallback((targetIndex) => {
    const renderingEngine = renderingEngineRef.current;
    if (!renderingEngine) return;
    try {
      const viewport = renderingEngine.getViewport(viewportId);
      if (!viewport || typeof viewport.setImageIdIndex !== 'function') return;
      const total = totalSlicesRef.current || 0;
      if (total <= 1) return;
      const clamped = Math.max(0, Math.min(targetIndex, total - 1));
      viewport.setImageIdIndex(clamped);
      viewport.render();
      setCurrentSlice(clamped);
      const { voiRange: vr } = viewport.getProperties();
      if (vr) {
        setVoiRange({
          windowWidth: vr.upper - vr.lower,
          windowCenter: (vr.upper + vr.lower) / 2,
        });
      }
    } catch (error) {
      console.error(`[StackRadiologyViewer ${instanceId}] Scrollbar change failed:`, error);
    }
  }, [viewportId, instanceId]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      <RadiologyToolbar
        activeTool={activeTool}
        onToolClick={handleToolClick}
        syncDisabled={true}
        mprDisabled={true}
        syncTooltipText="Synchronisation (désactivée)"
        isFullscreen={isFullscreen}
        onFullscreenToggle={handleFullscreenToggle}
        mipDisabled={true}
        mipTooltipText="MIP non disponible en mode CPU"
        onMipToggle={() => {}}
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          padding: '8px',
          borderRadius: '4px',
        }}
      />

      {/* Viewer Container */}
      <div
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Spin size="large" />
              <div style={{ fontSize: 12, color: "#1f1f1f" }}>
                {formatLoadProgress(loadProgress)}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1000,
              flexDirection: "column",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "red" }}>Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Cornerstone viewport element */}
        <div
          ref={viewerRef}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#000",
          }}
          data-viewport-id={viewportId}
        />

        {/* Fallback mode indicator */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "40px",
            padding: "5px 10px",
            backgroundColor: "rgba(255, 165, 0, 0.7)",
            color: "white",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: 1000,
          }}
        >
          CPU Fallback Mode - Advanced features disabled
        </div>

        {/* Viewer Info - Slice indicator, etc. */}
        <RadiologyViewerInfo
          sliceIndex={currentSlice + 1}
          totalSlices={totalSlicesRef.current || 0}
          windowWidth={voiRange?.windowWidth || 400}
          windowCenter={voiRange?.windowCenter || 40}
        />

        <SliceScrollbar
          currentSlice={currentSlice}
          totalSlices={totalSlicesRef.current || 0}
          onSliceChange={handleScrollbarChange}
        />
      </div>
    </div>
  );
};

export default StackRadiologyViewer; 